import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, formatMoney, fxRate, hoy, newId, parseMoney, usdToCordobas, type CuentaBanco } from '@abastece/shared';
import { CATEGORIAS_GASTO, type CategoriaGasto } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { conCuentasPuestas, cuentaQueQueda, nombreDeCuenta } from '../lib/bancos.js';
import { emitirYVer } from '../lib/emitir.js';
import { etiquetaCategoriaGasto } from '../i18n/dominio.js';
import { useT } from '../i18n/index.js';
import { Respuestas, type Paso } from './Asistente.js';
import { Campo } from './Campo.js';
import { ElegirEscribiendo } from './ElegirEscribiendo.js';
import { Dato, DatoTexto, enDolares, fechaCorta } from './CuentasDeCaja.js';
import { ElegirCuenta } from './ElegirCuenta.js';
import { TomarFotoCedula } from './FotoCedula.js';
import { Ico, type NombreIcono } from './Iconos.js';
import { faltaEnGaveta, useGaveta } from './SaleDeLaGaveta.js';
import { enRango } from '../lib/costos.js';

/**
 * An expense, a question to a screen: what it was for, how much and on what day, what exactly, how
 * it was paid, and a photo of the receipt; then a check, and it is recorded. The steps of a wizard
 * that has an expense to write down in it (`routes/CuadrarSemana.tsx`): what «Gastos» records,
 * asked the way the till asks for a count. The day, because a week is gone over at its end, and
 * Monday's water bill is Monday's.
 */

type Forma = 'efectivo_cordobas' | 'efectivo_dolares' | 'transferencia' | 'billetera' | 'credito';

interface Borrador {
  categoria: CategoriaGasto | null;
  descripcion: string;
  monto: string;
  moneda: 'NIO' | 'USD';
  /** The day it was paid; today unless said. */
  fecha: string;
  forma: Forma | null;
  /** Cash in córdobas with a till open on this device: out of its drawer, or out of general cash. */
  deLaGaveta: boolean;
  proveedorId: string;
  referencia: string;
  foto: string | null;
  /** By transfer, the bank account picked; null for where transfers go. */
  cuenta: CuentaBanco | null;
}

const NUEVO: Borrador = {
  categoria: null,
  descripcion: '',
  monto: '',
  moneda: 'NIO',
  fecha: '',
  forma: null,
  deLaGaveta: false,
  proveedorId: '',
  referencia: '',
  foto: null,
  cuenta: null,
};

const ICONO_CATEGORIA: Record<CategoriaGasto, NombreIcono> = {
  alquiler: 'negocio',
  energia: 'rapido',
  agua: 'semilla',
  telefono_internet: 'telefono',
  transporte: 'reparto',
  mantenimiento: 'ajustes',
  impuestos_municipales: 'bitacora',
  empaques: 'productos',
  sueldos: 'personal',
  otros: 'gastos',
};

/** The steps' ids, in the order they come. Each wizard puts them where its expenses go. */
export const PASOS_DE_GASTO = [
  'gasto-categoria',
  'gasto-monto',
  'gasto-descripcion',
  'gasto-pago',
  'gasto-proveedor',
  'gasto-referencia',
  'gasto-foto',
  'gasto-revise',
] as const;

/**
 * The steps, and how to start them. `ir` moves the wizard to one of them; `alGuardar` is told once
 * the expense is recorded, with its id, and the wizard goes on from there.
 */
export function usePasosDeGasto({ ir, alGuardar }: { ir: (id: string) => void; alGuardar: (gastoId: string) => void }): {
  pasos: Paso[];
  empezar: () => void;
} {
  const t = useT();
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const proveedores = useLiveQuery(() => db.proveedores.toArray(), [], []);
  const [b, setB] = useState<Borrador>(NUEVO);
  const poner = (parte: Partial<Borrador>) => setB((x) => ({ ...x, ...parte }));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gaveta = useGaveta('efectivo_cordobas');

  const monto = parseMoney(b.monto);
  const montoValido = monto !== null && monto > 0;
  const fecha = b.fecha || hoy();
  const fechaValida = fecha <= hoy();
  /**
   * The open till's drawer pays only what is paid today: an expense of another day paid from it
   * would come off today's till, which would then come up over by it, and that day's short.
   */
  const hayGaveta = gaveta.aplica && fecha === hoy();
  const enCordobas = montoValido ? (b.moneda === 'USD' ? enRango(() => usdToCordobas(monto, fxRate(config.fxTasaOficial))) : monto) : null;
  const deGaveta = b.forma === 'efectivo_cordobas' && hayGaveta && b.deLaGaveta;
  const conProveedor = b.forma === 'credito';
  const conReferencia = b.forma === 'transferencia' || b.forma === 'billetera';
  /** By transfer, the account it comes from: a dollar account only for an expense in dollars. */
  const cuenta = b.forma === 'transferencia' ? cuentaQueQueda(config, b.cuenta, b.moneda) : null;
  const nombreProveedor = proveedores.find((p) => p.id === b.proveedorId)?.nombre;

  /** The ones this expense goes through: whom it is owed to only on credit, a number only for a transfer. */
  const ids = PASOS_DE_GASTO.filter(
    (id) => (id !== 'gasto-proveedor' || conProveedor) && (id !== 'gasto-referencia' || conReferencia),
  );
  const despues = (id: string) => ids[ids.indexOf(id as (typeof ids)[number]) + 1] ?? id;
  const seguir = (id: string, deshabilitado = false) => ({
    etiqueta: t('asistente.siguiente'),
    deshabilitado,
    alTocar: () => ir(despues(id)),
  });

  function pagarCon(forma: Forma, deLaGaveta = false) {
    poner({ forma, deLaGaveta });
    setError(null);
    ir(forma === 'credito' ? 'gasto-proveedor' : forma === 'transferencia' || forma === 'billetera' ? 'gasto-referencia' : 'gasto-foto');
  }

  async function guardar() {
    if (!b.categoria || !b.forma || !montoValido || !fechaValida || !b.descripcion.trim() || enCordobas === null) return;
    // The drawer's own check, the one «Gastos» makes: the books refuse what the drawer cannot pay.
    const problema = deGaveta ? faltaEnGaveta(gaveta, enCordobas, t) : null;
    if (problema) {
      setError(problema);
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      const forma: Forma = b.forma === 'efectivo_cordobas' && b.moneda === 'USD' ? 'efectivo_dolares' : b.forma;
      const gastoId = newId();
      const { rechazo } = await emitirYVer('gasto.registrado', {
        gastoId,
        fecha,
        categoria: b.categoria,
        descripcion: b.descripcion.trim(),
        moneda: b.moneda,
        monto: monto!,
        // The official rate: what dollars are carried at on the books, general cash included.
        ...(b.moneda === 'USD' ? { tasaCambio: config.fxTasaOficial } : {}),
        formaPago: forma,
        ...(deGaveta && gaveta.sesionId ? { sesionId: gaveta.sesionId } : {}),
        ...(cuenta ? { cuenta } : {}),
        proveedorId: b.proveedorId || null,
        ...(conReferencia && b.referencia.trim() ? { referencia: b.referencia.trim() } : {}),
        ...(b.foto ? { foto: b.foto } : {}),
      });
      if (rechazo) {
        setError(rechazo);
        return;
      }
      setB(NUEVO);
      alGuardar(gastoId);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setGuardando(false);
    }
  }

  const montoEscrito = montoValido ? (b.moneda === 'USD' ? enDolares(monto!) : formatMoney(monto!)) : '';
  const comoSePago: string =
    b.forma === null
      ? ''
      : b.forma === 'efectivo_cordobas'
        ? t(deGaveta ? 'gastoPaso.deLaGaveta' : 'gastoPaso.delGeneral')
        : t(
            b.forma === 'transferencia'
              ? 'gastoPaso.transferencia'
              : b.forma === 'billetera'
                ? 'gastoPaso.billetera'
                : 'gastoPaso.credito',
          );

  const todos: Record<(typeof PASOS_DE_GASTO)[number], Paso> = {
    'gasto-categoria': {
      id: 'gasto-categoria',
      titulo: t('gastoPaso.categoria'),
      explicacion: <p>{t('gastoPaso.categoriaExplicacion')}</p>,
      contenido: (
        <Respuestas
          opciones={CATEGORIAS_GASTO.map((c) => ({
            etiqueta: etiquetaCategoriaGasto(c),
            icono: ICONO_CATEGORIA[c],
            elegida: b.categoria === c,
            alTocar: () => {
              // What it was for says what it was, until somebody says more: «Agua» for the water bill.
              const anterior = b.categoria ? etiquetaCategoriaGasto(b.categoria) : '';
              poner({ categoria: c, ...(b.descripcion.trim() === '' || b.descripcion === anterior ? { descripcion: etiquetaCategoriaGasto(c) } : {}) });
              ir('gasto-monto');
            },
          }))}
        />
      ),
    },
    'gasto-monto': {
      id: 'gasto-monto',
      titulo: t('gastoPaso.monto'),
      explicacion: <p>{t('gastoPaso.montoExplicacion')}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3" role="group" aria-label={t('gastos.moneda')}>
            {(['NIO', 'USD'] as const).map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={b.moneda === m}
                className={`btn-secundario ${b.moneda === m ? 'ring-2 ring-marca-400' : ''}`}
                onClick={() => poner({ moneda: m })}
              >
                <Ico icono={m === 'USD' ? 'dolares' : 'cobrar'} />
                {t(m === 'USD' ? 'gastos.dolares' : 'gastos.cordobas')}
              </button>
            ))}
          </div>
          <Campo
            etiqueta={t(b.moneda === 'USD' ? 'gastos.montoDolares' : 'gastos.montoCordobas')}
            inputMode="decimal"
            className="tabular text-lg"
            value={b.monto}
            onChange={(e) => poner({ monto: e.target.value })}
            placeholder="0.00"
            autoComplete="off"
            error={b.monto.trim() !== '' && !montoValido ? t('caja.montoInvalido') : null}
            {...(b.moneda === 'USD' ? { ayuda: t('gastos.aTasa', { tasa: (config.fxTasaOficial / 10_000).toFixed(4) }) } : {})}
          />
          <Campo
            etiqueta={t('gastoPaso.fecha')}
            type="date"
            value={fecha}
            max={hoy()}
            onChange={(e) => poner({ fecha: e.target.value })}
            error={fechaValida ? null : t('gastoPaso.fechaFutura')}
          />
        </div>
      ),
      seguir: seguir('gasto-monto', !montoValido || !fechaValida),
    },
    'gasto-descripcion': {
      id: 'gasto-descripcion',
      titulo: t('gastoPaso.descripcion'),
      explicacion: <p>{t('gastoPaso.descripcionExplicacion')}</p>,
      contenido: (
        <Campo
          maxLength={200}
          etiqueta={t('gastos.deQueFue')}
          value={b.descripcion}
          onChange={(e) => poner({ descripcion: e.target.value })}
          autoComplete="off"
          ayuda={t('gastos.ayudaDescripcion')}
        />
      ),
      seguir: seguir('gasto-descripcion', b.descripcion.trim() === ''),
    },
    'gasto-pago': {
      id: 'gasto-pago',
      titulo: t('gastoPaso.pago'),
      explicacion: <p>{t('gastoPaso.pagoExplicacion')}</p>,
      contenido: (
        <Respuestas
          opciones={[
            // With a till open here, córdobas in cash come out of its drawer or out of the safe.
            ...(hayGaveta && b.moneda === 'NIO'
              ? [
                  {
                    etiqueta: t('gastoPaso.efectivoGaveta'),
                    detalle: t('gastoPaso.efectivoGavetaDetalle'),
                    icono: 'caja' as const,
                    elegida: b.forma === 'efectivo_cordobas' && b.deLaGaveta,
                    alTocar: () => pagarCon('efectivo_cordobas', true),
                  },
                ]
              : []),
            {
              etiqueta: t('gastoPaso.efectivo'),
              detalle: t('gastoPaso.efectivoDetalle'),
              icono: b.moneda === 'USD' ? 'dolares' : 'cobrar',
              elegida: b.forma === 'efectivo_cordobas' && !b.deLaGaveta,
              alTocar: () => pagarCon('efectivo_cordobas'),
            },
            {
              etiqueta: t('gastoPaso.transferencia'),
              detalle: t('gastoPaso.transferenciaDetalle'),
              icono: 'transferencias',
              elegida: b.forma === 'transferencia',
              alTocar: () => pagarCon('transferencia'),
            },
            {
              etiqueta: t('gastoPaso.billetera'),
              detalle: t('gastoPaso.billeteraDetalle'),
              icono: 'billetera',
              elegida: b.forma === 'billetera',
              alTocar: () => pagarCon('billetera'),
            },
            {
              etiqueta: t('gastoPaso.credito'),
              detalle: t('gastoPaso.creditoDetalle'),
              icono: 'despues',
              elegida: b.forma === 'credito',
              alTocar: () => pagarCon('credito'),
            },
          ]}
        />
      ),
    },
    'gasto-proveedor': {
      id: 'gasto-proveedor',
      titulo: t('gastoPaso.proveedor'),
      explicacion: <p>{t('gastoPaso.proveedorExplicacion')}</p>,
      contenido: (
        <ElegirEscribiendo
          etiqueta={t('gastos.proveedorOpcional')}
          placeholder={t('gastos.nadieEnParticular')}
          opciones={proveedores.filter((p) => p.activo || p.id === b.proveedorId)}
          value={b.proveedorId}
          onChange={(proveedorId) => poner({ proveedorId })}
          ninguno={t('gastos.nadieEnParticular')}
          datos="proveedor"
        />
      ),
      seguir: seguir('gasto-proveedor'),
    },
    'gasto-referencia': {
      id: 'gasto-referencia',
      titulo: t('gastoPaso.referencia'),
      explicacion: <p>{t('gastoPaso.referenciaExplicacion')}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          <Campo
            maxLength={80}
            etiqueta={t('comun.referencia')}
            value={b.referencia}
            onChange={(e) => poner({ referencia: e.target.value })}
            autoComplete="off"
          />
          {cuenta && <ElegirCuenta moneda={b.moneda} valor={cuenta} onCambiar={(c) => poner({ cuenta: c })} etiqueta={t('bancos.pagaDe')} />}
        </div>
      ),
      seguir: seguir('gasto-referencia'),
    },
    'gasto-foto': {
      id: 'gasto-foto',
      titulo: t('gastoPaso.foto'),
      explicacion: <p>{t('gastoPaso.fotoExplicacion')}</p>,
      contenido: (
        <TomarFotoCedula
          huella={b.foto}
          onCambio={(foto) => poner({ foto })}
          textos={{
            etiqueta: t('gastos.fotoRecibo'),
            ayuda: t('gastos.ayudaFotoRecibo'),
            tomar: t('gastos.tomarFotoRecibo'),
            cambiar: t('gastos.cambiarFotoRecibo'),
          }}
        />
      ),
      seguir: seguir('gasto-foto'),
    },
    'gasto-revise': {
      id: 'gasto-revise',
      titulo: t('turno.revise'),
      explicacion: <p>{t('gastoPaso.reviseExplicacion')}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          <div className="tarjeta flex flex-col gap-2">
            <DatoTexto etiqueta={t('gastos.categoria')} valor={b.categoria ? etiquetaCategoriaGasto(b.categoria) : ''} />
            <DatoTexto etiqueta={t('gastos.deQueFue')} valor={b.descripcion.trim()} />
            <Dato etiqueta={t('gastoPaso.cuanto')} valor={montoEscrito} />
            <Dato etiqueta={t('cuadrar.fecha')} valor={fechaCorta(fecha)} />
            <DatoTexto etiqueta={t('gastos.formaPago')} valor={comoSePago} />
            {cuenta && conCuentasPuestas(config) && <DatoTexto etiqueta={t('bancos.pagaDe')} valor={nombreDeCuenta(config, cuenta, t)} />}
            {conProveedor && <DatoTexto etiqueta={t('gastos.proveedor')} valor={nombreProveedor ?? t('gastos.nadieEnParticular')} />}
            {conReferencia && b.referencia.trim() && <DatoTexto etiqueta={t('comun.referencia')} valor={b.referencia.trim()} />}
            <Dato etiqueta={t('gastos.fotoRecibo')} valor={t(b.foto ? 'gastoPaso.conFoto' : 'gastoPaso.sinFoto')} />
          </div>
          {error && <p className="text-sm text-rose-400">{error}</p>}
        </div>
      ),
      seguir: {
        etiqueta: t('gastos.guardar'),
        icono: 'guardar',
        deshabilitado: guardando || !b.categoria || !b.forma || !montoValido || !fechaValida || !b.descripcion.trim(),
        alTocar: () => void guardar(),
      },
    },
  };

  return {
    pasos: ids.map((id) => todos[id]),
    empezar: () => {
      setB(NUEVO);
      setError(null);
      ir('gasto-categoria');
    },
  };
}
